import "./cell/tabular-grid-cell";
import "./row/tabular-grid-row";
import "./rowset/tabular-grid-rowset";
import { Host, h } from "@stencil/core";
import { MouseEventButton, MouseEventButtons, focusComposedPath, mouseEventHasButtonPressed, mouseEventModifierKey } from "../common/helpers";
import { TabularGridManager } from "./tabular-grid-manager";
/**
 * The `ch-tabular-grid` component represents a Grid/TreeGrid of data, with rows and cells.
 */
export class ChTabularGrid {
    constructor() {
        this.rowFocused = undefined;
        this.cellFocused = undefined;
        this.rowHighlighted = undefined;
        this.rowsMarked = [];
        this.rowsSelected = [];
        this.cellSelected = undefined;
        this.gridStyle = undefined;
        this.rowSelectionMode = "single";
        this.keyboardNavigationMode = "select";
        this.rowHighlightEnabled = "auto";
        this.rowSelectedClass = undefined;
        this.rowHighlightedClass = undefined;
        this.rowFocusedClass = undefined;
        this.rowMarkedClass = undefined;
        this.showLines = "all";
        this.allowColumnReorder = true;
        this.columnResizeMode = "single";
        this.localization = undefined;
    }
    manager;
    gridLayoutElement;
    settingsUI;
    el;
    rowFocusedHandler(row, previous) {
        if (row) {
            row.focused = true;
        }
        if (previous) {
            previous.focused = false;
        }
    }
    cellFocusedHandler(cell, previous) {
        if (cell) {
            cell.focused = true;
        }
        if (previous) {
            previous.focused = false;
        }
    }
    rowHighlightedHandler(row, previous) {
        // highlight
        if (row) {
            row.highlighted = true;
        }
        if (previous) {
            previous.highlighted = false;
        }
        // actions
        if (row) {
            this.manager.rowActions.showOnRowHover?.openRowHover(row);
        }
        else {
            this.manager.rowActions.showOnRowHover?.close();
        }
    }
    rowsMarkedHandler(rows, previous) {
        this.manager.selection.syncRowSelector(rows, previous, "mark");
        this.rowMarkingChanged.emit(this.getChangedEventDetail(rows, previous));
    }
    rowsSelectedHandler(rows, previous) {
        if (previous) {
            previous
                .filter(row => !rows.includes(row))
                .forEach(row => (row.selected = false));
        }
        if (rows) {
            rows.forEach(row => (row.selected = true));
        }
        this.manager.selection.syncRowSelector(rows, previous, "select");
        this.selectionChanged.emit(this.getChangedEventDetail(rows, previous));
    }
    cellSelectedHandler(cell, previous) {
        if (cell) {
            cell.selected = true;
        }
        if (previous) {
            previous.selected = false;
        }
        this.cellSelectionChanged.emit({
            cellId: this.cellSelected ? this.cellSelected.cellId : null,
            rowId: this.cellSelected ? this.cellSelected.row.rowId : null,
            columnId: this.cellSelected ? this.cellSelected.column.columnId : null
        });
    }
    /**
     * Event emitted when the row selection is changed.
     */
    selectionChanged;
    /**
     * Event emitted when the row marking is changed.
     */
    rowMarkingChanged;
    /**
     * Event emitted when the cell selection is changed.
     */
    cellSelectionChanged;
    /**
     * Event emitted when a row is clicked.
     */
    rowClicked;
    /**
     * Event emitted when a row is double clicked.
     */
    rowDoubleClicked;
    /**
     * Event emitted when Enter is pressed on a row.
     */
    rowEnterPressed;
    /**
     * Event emitted when attempts to open a context menu on a row.
     */
    rowContextMenu;
    componentWillLoad() {
        this.manager = new TabularGridManager(this.el);
        this.gridStyle = this.manager.getGridStyle();
    }
    componentDidLoad() {
        this.manager.componentDidLoad(this.gridLayoutElement);
    }
    // @ts-expect-error: TODO: Fix this error
    componentShouldUpdate(_newValue, _oldValue, name) {
        if (name === "rowFocused" ||
            name === "rowHighlighted" ||
            name === "rowSelected" ||
            name === "rowsMarked" ||
            name === "cellSelected") {
            return false;
        }
    }
    focusHandler() {
        if (this.keyboardNavigationMode !== "none") {
            this.rowFocused ||= this.rowsSelected[0] ?? this.manager.getFirstRow();
            this.cellFocused = this.rowFocused?.getCell(this.cellSelected?.column || this.manager.getFirstColumn());
            if (this.keyboardNavigationMode === "select") {
                this.selectByKeyboardEvent(false, false, "focus");
            }
        }
    }
    blurHandler(eventInfo) {
        if (!eventInfo.composedPath().includes(this.el)) {
            this.rowFocused = null;
            this.cellFocused = null;
        }
    }
    cellFocusedEventHandler(eventInfo) {
        const cell = eventInfo.target;
        if (this.rowSelectionMode !== "none" && !cell.selected) {
            this.setCellSelected(cell);
        }
    }
    windowKeyDownHandler(eventInfo) {
        if (focusComposedPath()[0] === this.el &&
            [
                " ",
                "+",
                "-",
                "Home",
                "End",
                "PageUp",
                "PageDown",
                "ArrowUp",
                "ArrowDown",
                "ArrowLeft",
                "ArrowRight"
            ].includes(eventInfo.key)) {
            eventInfo.preventDefault();
        }
    }
    keyDownHandler(eventInfo) {
        if (focusComposedPath()[0] === this.el &&
            this.keyboardNavigationMode !== "none") {
            const range = eventInfo.shiftKey;
            const append = mouseEventModifierKey(eventInfo);
            switch (eventInfo.key) {
                case " ":
                    this.spacePressedEvent(mouseEventModifierKey(eventInfo), eventInfo.shiftKey);
                    break;
                case "+":
                    this.setRowCollapsed(this.rowFocused, false);
                    break;
                case "-":
                    this.setRowCollapsed(this.rowFocused, true);
                    break;
                case "Home":
                    this.moveByKeyboardEvent(this.manager.selection.moveFirstRow, range, append);
                    break;
                case "End":
                    this.moveByKeyboardEvent(this.manager.selection.moveLastRow, range, append);
                    break;
                case "PageUp":
                    this.moveByKeyboardEvent(this.manager.selection.movePreviousPageRow, range, append);
                    break;
                case "PageDown":
                    this.moveByKeyboardEvent(this.manager.selection.moveNextPageRow, range, append);
                    break;
                case "ArrowUp":
                    this.moveByKeyboardEvent(this.manager.selection.movePreviousRow, range, append);
                    break;
                case "ArrowDown":
                    this.moveByKeyboardEvent(this.manager.selection.moveNextRow, range, append);
                    break;
                case "ArrowLeft":
                    this.moveByKeyboardEvent(this.manager.selection.movePreviousCell, range, append);
                    break;
                case "ArrowRight":
                    this.moveByKeyboardEvent(this.manager.selection.moveNextCell, range, append);
                    break;
                case "Enter":
                    this.enterPressedHandler();
                    break;
            }
        }
    }
    mouseMoveHandler(eventInfo) {
        if ((this.rowHighlightEnabled === "auto" &&
            this.rowSelectionMode !== "none") ||
            this.rowHighlightEnabled === true) {
            this.rowHighlighted =
                this.manager.getRowEventTarget(eventInfo) ||
                    (this.manager.isRowActionsEventTarget(eventInfo)
                        ? this.rowHighlighted
                        : null);
        }
        selectingBlock: {
            if (this.manager.selection.selecting) {
                if (focusComposedPath()[0] !== this.el) {
                    this.stopSelecting();
                    break selectingBlock;
                }
                const row = this.manager.getRowEventTarget(eventInfo);
                const cell = this.manager.getCellEventTarget(eventInfo);
                if (row &&
                    (this.manager.selection.selectingRow !== row ||
                        this.manager.selection.selectingCell !== cell)) {
                    const isKeyModifierPressed = mouseEventModifierKey(eventInfo);
                    const isMouseButtonRightPressed = mouseEventHasButtonPressed(eventInfo, MouseEventButtons.RIGHT);
                    this.selectByPointerEvent(row, cell, isKeyModifierPressed && !isMouseButtonRightPressed, !isMouseButtonRightPressed, isMouseButtonRightPressed);
                    this.manager.selection.selectingRow = row;
                    this.manager.selection.selectingCell = cell;
                }
            }
        }
    }
    mouseLeaveHandler() {
        this.rowHighlighted &&= null;
    }
    mouseDownHandler(eventInfo) {
        const row = this.manager.getRowEventTarget(eventInfo);
        const cell = this.manager.getCellEventTarget(eventInfo);
        if (row) {
            this.manager.selection.selecting = true;
            this.selectByPointerEvent(row, cell, mouseEventModifierKey(eventInfo), eventInfo.shiftKey, eventInfo.button === MouseEventButton.RIGHT);
        }
    }
    mouseUpHandler() {
        if (this.manager.selection.selecting) {
            this.stopSelecting();
        }
    }
    clickHandler(eventInfo) {
        const row = this.manager.getRowEventTarget(eventInfo);
        const cell = this.manager.getCellEventTarget(eventInfo);
        if (row) {
            this.emitRowClicked(row, cell);
        }
    }
    dblclickHandler(eventInfo) {
        const row = this.manager.getRowEventTarget(eventInfo);
        const cell = this.manager.getCellEventTarget(eventInfo);
        if (row) {
            this.rowDoubleClicked.emit({
                rowId: row.rowId,
                cellId: cell?.cellId,
                columnId: cell?.column.columnId
            });
        }
    }
    touchstartHandler(eventInfo) {
        this.manager.selection.touchStart(eventInfo);
    }
    touchendHandler(eventInfo) {
        if (eventInfo.cancelable) {
            eventInfo.preventDefault();
        }
        if (this.manager.selection.isTouchEndSelection(eventInfo)) {
            const columnSelector = this.manager.columns.getColumnSelector();
            const row = this.manager.getRowEventTarget(eventInfo);
            const cell = this.manager.getCellEventTarget(eventInfo);
            if (row) {
                this.selectByPointerEvent(row, cell, columnSelector?.richRowSelectorMode !== "select", false, false);
                this.emitRowClicked(row, cell);
            }
        }
    }
    contextmenuHandler(eventInfo) {
        let targetRow;
        if (eventInfo.target === this.el &&
            eventInfo.button === MouseEventButton.KEY_SHORTCUT) {
            targetRow = this.rowFocused;
        }
        else {
            targetRow = this.manager.getRowEventTarget(eventInfo);
        }
        if (targetRow) {
            const cellFocused = this.cellSelected?.row === targetRow ? this.cellSelected : null;
            const rowContextMenuEventInfo = this.rowContextMenu.emit({
                rowId: targetRow.rowId,
                cellId: cellFocused?.cellId,
                columnId: cellFocused?.column.columnId,
                selectedRowsId: this.rowsSelected.map(row => row.rowId),
                clientX: eventInfo.clientX,
                clientY: eventInfo.clientY
            });
            this.manager.rowActions.showOnRowContext?.openRowContext(eventInfo.clientX, eventInfo.clientY);
            if (rowContextMenuEventInfo.defaultPrevented ||
                this.manager.rowActions.showOnRowContext) {
                eventInfo.preventDefault();
            }
        }
    }
    columnSelectorClickedHandler(eventInfo) {
        const columnSelector = this.manager.columns.getColumnSelector();
        if (columnSelector?.richRowSelectorMode === "select") {
            this.selectAll(eventInfo.detail.checked);
        }
        else if (columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markAllRows(eventInfo.detail.checked);
        }
    }
    cellSelectorClickedHandler(eventInfo) {
        const columnSelector = this.manager.columns.getColumnSelector();
        if (columnSelector?.richRowSelectorMode === "select") {
            this.selectByPointerEvent(this.manager.getRowEventTarget(eventInfo), this.manager.getCellEventTarget(eventInfo), true, eventInfo.detail.range, false);
        }
        else if (columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markRow(this.manager.getRowEventTarget(eventInfo), eventInfo.detail.checked, eventInfo.detail.range, this.rowsMarked);
        }
    }
    cellRowActionClickedHandler(eventInfo) {
        const cell = eventInfo.target;
        this.manager.rowActions.showOnRowActions?.openRowActions(cell);
    }
    columnStyleChangedHandler() {
        if (this.manager) {
            this.gridStyle = this.manager.getGridStyle();
        }
    }
    columnFreezeChangedHandler() {
        this.manager.columns.adjustFreezeOrder();
    }
    columnResizeStartedHandler(eventInfo) {
        this.manager.columnResizeStart(eventInfo.detail.columnId);
    }
    columnResizingHandler(eventInfo) {
        this.manager.columnResizing(eventInfo.detail.deltaWidth);
    }
    columnResizeFinishedHandler() {
        this.manager.columnResizeEnd();
    }
    columnDragStartHandler(eventInfo) {
        this.manager.columnDragStart(eventInfo.detail.columnId);
    }
    columnDraggingHandler(eventInfo) {
        if (this.manager.columnDragging(eventInfo.detail.positionX)) {
            this.gridStyle = this.manager.getGridStyle();
        }
    }
    columnDragEndHandler() {
        this.manager.columnDragEnd();
        this.gridStyle = this.manager.getGridStyle();
    }
    rowDragStartHandler(eventInfo) {
        this.manager.rowDragStart(eventInfo.detail.row);
    }
    rowEnsureVisibleHandler(eventInfo) {
        this.manager.ensureRowVisible(eventInfo.target);
    }
    cellEnsureVisibleHandler(eventInfo) {
        this.manager.ensureCellVisible(eventInfo.target);
    }
    settingsShowClickedHandler() {
        this.settingsUI.show = true;
    }
    settingsCloseClickedHandler() {
        this.settingsUI.show = false;
    }
    /**
     * Retrieves the rowId of the currently focused row.
     */
    async getFocusedRow() {
        return this.rowFocused?.rowId;
    }
    /**
     * Retrieves the rowId of the currently hovered row.
     */
    async getHoveredRow() {
        return this.rowHighlighted?.rowId;
    }
    /**
     * Retrieves the list of rowId of the selected rows.
     */
    async getSelectedRows() {
        return this.rowsSelected.map(row => row.rowId);
    }
    /**
     * Retrieves the list of rowId of the marked rows.
     */
    async getMarkedRows() {
        return this.rowsMarked.map(row => row.rowId);
    }
    /**
     * Retrieves information about the currently focused cell.
     */
    async getFocusedCell() {
        return {
            cellId: this.cellFocused ? this.cellFocused.cellId : null,
            rowId: this.cellFocused ? this.cellFocused.row.rowId : null,
            columnId: this.cellFocused ? this.cellFocused.column.columnId : null
        };
    }
    /**
     * Retrieves information about the currently selected cell.
     */
    async getSelectedCell() {
        return {
            cellId: this.cellSelected ? this.cellSelected.cellId : null,
            rowId: this.cellSelected ? this.cellSelected.row.rowId : null,
            columnId: this.cellSelected ? this.cellSelected.column.columnId : null
        };
    }
    /**
     * Selects or deselects a row.
     * @param rowId - The rowId of the row to select or deselect.
     * @param selected - A boolean indicating whether to select or deselect the row.
     */
    async selectRow(rowId, selected = true) {
        const row = this.manager.getRow(rowId);
        if (row) {
            const { rowFocused, rowsSelected, cellFocused, cellSelected } = this.manager.selection.selectSet({
                rowFocused: this.rowFocused,
                rowsSelected: this.rowsSelected,
                cellFocused: this.cellFocused,
                cellSelected: this.cellSelected
            }, row, null, selected);
            this.rowFocused = rowFocused;
            this.rowsSelected = rowsSelected;
            this.cellFocused = cellFocused;
            this.cellSelected = cellSelected;
            rowFocused?.ensureVisible();
        }
    }
    /**
     * Selects or deselects all rows.
     * @param selected - A boolean indicating whether to select or deselect all rows.
     */
    async selectAllRows(selected = true) {
        this.selectAll(selected);
    }
    /**
     * Select or deselect a cell.
     * The cell can be identified by the cellId parameter or
     * by using the rowId and columnId pair.
     * @param cellId - The cellId of the cell to select or deselect.
     * @param rowId - The rowId of the row containing the cell.
     * @param columnId - The columnId of the column containing the cell.
     * @param selected - A boolean indicating whether to select or deselect the cell.
     */
    async selectCell(cellId, rowId, columnId, selected = true) {
        const cell = this.manager.getCell(cellId, rowId, columnId);
        if (cell) {
            this.setCellSelected(cell, selected);
        }
    }
    /**
     * Mark or unmark a row.
     * @param rowId - The rowId of the row to select or deselect.
     * @param marked - A boolean indicating whether to mark or unmark the row.
     */
    async markRow(rowId, marked = true) {
        const columnSelector = this.manager.columns.getColumnSelector();
        const row = this.manager.getRow(rowId);
        if (row && columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markRow(row, marked, false, this.rowsMarked);
        }
    }
    /**
     * Mark or unmark all rows.
     * @param marked - A boolean indicating whether to mark or unmark all rows.
     */
    async markAllRows(marked = true) {
        const columnSelector = this.manager.columns.getColumnSelector();
        if (columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markAllRows(marked);
        }
    }
    /**
     * Expands a row, showing its children.
     * @param rowId - The rowId of the row to expand.
     */
    async expandRow(rowId) {
        this.setRowCollapsed(this.manager.getRow(rowId), false);
    }
    /**
     * Collapses a row, hiding its children.
     * @param rowId - The rowId of the row to collapse.
     */
    async collapseRow(rowId) {
        this.setRowCollapsed(this.manager.getRow(rowId), true);
    }
    /**
     * Ensures that the row is visible within the control, scrolling the contents of the control if necessary.
     * @param rowId - The rowId of the row to ensure visibility.
     */
    async rowEnsureVisible(rowId) {
        const row = this.manager.getRow(rowId);
        if (row) {
            this.manager.ensureRowVisible(row);
        }
    }
    /**
     * Ensures that the cell is visible within the control, scrolling the contents of the control if necessary.
     * @param cellId - The cellId of the cell to ensure visibility.
     */
    async cellEnsureVisible(cellId) {
        const cell = this.manager.getCell(cellId);
        if (cell) {
            this.manager.ensureCellVisible(cell);
        }
    }
    /**
     * Retrieves the rowId of the previous row relative to the currently selected cell.
     */
    async getPreviousRow() {
        const currentRow = this.cellSelected?.row;
        if (currentRow) {
            return this.manager.getPreviousRow(currentRow)?.rowId || null;
        }
    }
    /**
     * Retrieves the rowId of the next row relative to the currently selected cell.
     */
    async getNextRow() {
        const currentRow = this.cellSelected?.row;
        if (currentRow) {
            return this.manager.getNextRow(currentRow)?.rowId || null;
        }
    }
    /**
     * Retrieves information about the previous cell relative to the currently selected cell.
     */
    async getPreviousCell() {
        const previousCell = this.manager.getPreviousCell(this.cellSelected);
        return {
            cellId: previousCell ? previousCell.cellId : null,
            rowId: previousCell ? previousCell.row.rowId : null,
            columnId: previousCell ? previousCell.column.columnId : null
        };
    }
    /**
     * Retrieves information about the next cell relative to the currently selected cell.
     */
    async getNextCell() {
        const nextCell = this.manager.getNextCell(this.cellSelected);
        return {
            cellId: nextCell ? nextCell.cellId : null,
            rowId: nextCell ? nextCell.row.rowId : null,
            columnId: nextCell ? nextCell.column.columnId : null
        };
    }
    /**
     * Synchronizes the state of a row in the grid.
     */
    async syncRowState(el) {
        const row = el;
        const columnSelector = this.manager.columns.getColumnSelector();
        if (this.rowSelectionMode !== "none") {
            this.syncRowStateSelected(row);
        }
        if (columnSelector?.richRowSelectorMode === "mark") {
            this.syncRowStateMarked(row);
        }
        if (columnSelector) {
            this.syncRowStateSelector(row, columnSelector);
            if (columnSelector.richRowSelectorMode === "select") {
                this.manager.selection.syncColumnSelector(this.rowsSelected.length, columnSelector);
            }
            else if (columnSelector.richRowSelectorMode === "mark") {
                this.manager.selection.syncColumnSelector(this.rowsMarked.length, columnSelector);
            }
        }
    }
    syncRowStateSelected(row) {
        if (row.selected && this.rowSelectionMode === "single") {
            this.rowsSelected[0] = row;
        }
        if (row.selected &&
            this.rowSelectionMode === "multiple" &&
            !this.rowsSelected.includes(row)) {
            this.rowsSelected.push(row);
        }
        if (!row.selected) {
            const index = this.rowsSelected.indexOf(row);
            if (index !== -1) {
                this.rowsSelected.splice(index, 1);
            }
        }
    }
    syncRowStateMarked(row) {
        if (row.marked && !this.rowsMarked.includes(row)) {
            this.rowsMarked.push(row);
        }
        if (!row.marked) {
            const index = this.rowsMarked.indexOf(row);
            if (index !== -1) {
                this.rowsMarked.splice(index, 1);
            }
        }
    }
    syncRowStateSelector(row, columnSelector) {
        const cell = row.getCell(columnSelector);
        const value = (columnSelector.richRowSelectorMode === "select" && row.selected) ||
            (columnSelector.richRowSelectorMode === "mark" && row.marked);
        cell.setSelectorChecked(value);
    }
    getChangedEventDetail(rows, previous) {
        return {
            rowsId: rows.map(row => row.rowId),
            addedRowsId: rows
                .filter(row => !previous.includes(row))
                .map(row => row.rowId),
            removedRowsId: previous
                .filter(row => !rows.includes(row))
                .map(row => row.rowId),
            unalteredRowsId: rows
                .filter(row => previous.includes(row))
                .map(row => row.rowId)
        };
    }
    enterPressedHandler() {
        if (this.rowFocused) {
            this.rowEnterPressed.emit({
                rowId: this.rowFocused.rowId,
                cellId: this.cellFocused ? this.cellFocused.cellId : null,
                columnId: this.cellFocused ? this.cellFocused.column.columnId : null
            });
        }
    }
    spacePressedEvent(ctrl, shift) {
        if (this.keyboardNavigationMode === "focus") {
            this.selectByKeyboardEvent(ctrl, shift, "last-selected");
        }
        else if (this.keyboardNavigationMode === "select") {
            this.markByKeyboardEvent();
        }
        this.emitRowClicked(this.rowFocused, this.cellFocused);
    }
    markByKeyboardEvent() {
        const columnSelector = this.manager.columns.getColumnSelector();
        if (columnSelector?.richRowSelectorMode === "mark") {
            this.rowsMarked = this.manager.selection.markRows(this.rowFocused, this.rowsMarked, this.rowsSelected);
        }
    }
    selectByKeyboardEvent(append, range, rangeStartOn) {
        const { rowFocused, rowsSelected, cellFocused, cellSelected } = this.manager.selection.select({
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellFocused: this.cellFocused,
            cellSelected: this.cellSelected
        }, this.rowFocused, this.cellFocused, true, append, range, rangeStartOn, false);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellFocused = cellFocused;
        this.cellSelected = cellSelected;
        (cellFocused || rowFocused)?.ensureVisible();
    }
    selectByPointerEvent(row, cell, append, range, context) {
        const { rowFocused, rowsSelected, cellFocused, cellSelected } = this.manager.selection.select({
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellFocused: this.cellFocused,
            cellSelected: this.cellSelected
        }, row, cell, true, append, range, "last-selected", context);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellFocused = cellFocused;
        this.cellSelected = cellSelected;
        (cellFocused || rowFocused)?.ensureVisible();
    }
    moveByKeyboardEvent(fn, range, append) {
        const { rowFocused, rowsSelected, cellFocused, cellSelected } = fn.call(this.manager.selection, {
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellFocused: this.cellFocused,
            cellSelected: this.cellSelected
        }, this.rowSelectionMode !== "none" &&
            this.keyboardNavigationMode === "select", this.rowSelectionMode === "multiple" ? range : false, this.rowSelectionMode === "multiple" ? range && append : false);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellFocused = cellFocused;
        this.cellSelected = cellSelected;
        (cellFocused || rowFocused)?.ensureVisible();
    }
    selectAll(value = true) {
        const { rowFocused, rowsSelected, cellFocused, cellSelected } = this.manager.selection.selectAll({
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellFocused: this.cellFocused,
            cellSelected: this.cellSelected
        }, value);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellFocused = cellFocused;
        this.cellSelected = cellSelected;
        (cellFocused || rowFocused)?.ensureVisible();
    }
    setRowCollapsed(row, collapsed) {
        if (row && collapsed) {
            if (row && row.hasChildRows) {
                row.collapsed = true;
            }
        }
        else if (row && !collapsed) {
            row.collapsed = false;
        }
    }
    setCellSelected(cell, selected = true) {
        const { rowFocused, rowsSelected, cellFocused, cellSelected } = this.manager.selection.selectSet({
            rowFocused: this.rowFocused,
            rowsSelected: this.rowsSelected,
            cellFocused: this.cellFocused,
            cellSelected: this.cellSelected
        }, cell.row, cell, selected);
        this.rowFocused = rowFocused;
        this.rowsSelected = rowsSelected;
        this.cellFocused = cellFocused;
        this.cellSelected = cellSelected;
        (cellFocused || rowFocused)?.ensureVisible();
    }
    emitRowClicked(row, cell) {
        this.rowClicked.emit({
            rowId: row.rowId,
            cellId: cell?.cellId,
            columnId: cell?.column.columnId
        });
    }
    stopSelecting() {
        this.manager.selection.selecting = false;
        this.manager.selection.selectingRow = null;
        this.manager.selection.selectingCell = null;
    }
    renderSettings() {
        return (h("ch-tabular-grid-settings", { grid: this.el, ref: el => (this.settingsUI = el), exportparts: "\n          mask:settings-mask,\n          window:settings-window,\n          header:settings-header,\n          caption:settings-caption,\n          close:settings-close,\n          main:settings-main,\n          footer:settings-footer\n        " }, h("slot", { name: "settings" }, h("ch-tabular-grid-settings-columns", { part: "settings-columns", columns: [...this.manager.getColumns()], exportparts: "\n              column:settings-columns-item,\n              column-label:settings-columns-label,\n              column-visible:settings-columns-visible,\n              column-visible-checked:settings-columns-visible-checked\n            " }))));
    }
    render() {
        return (h(Host, { key: '71032cbf048194d9b168df11f55bb0e9065bc291', tabindex: this.keyboardNavigationMode !== "none" ? "0" : false }, h("header", { key: '051074b9d1f5a054f2822f2bd35d52b6daae9f03', part: "header" }, h("slot", { key: '0700f24d8d5b143acbfd55ad32d60664136d1cb1', name: "header" })), h("section", { key: '2dcee9af72f0cefd502a97d7555e76febfa06e88', class: "main ch-scrollable", style: this.gridStyle, part: "main", ref: el => (this.gridLayoutElement = el) }, h("slot", { key: 'c444f3f17863ffc2e4b3c074761bcc35576ee9c8' })), h("aside", { key: '5c554f8a2e3c35d4a7a4967196c9931f172f8f14' }, this.renderSettings(), h("slot", { key: '5c5dc544b12e60fff90ddb79c7e2818aae5880e5', name: "column-display" }), h("slot", { key: '41e4253f37d1c488326364dc52d55f60b0b59b8e', name: "row-actions" })), h("footer", { key: '3967e4091f128877cc434614af099e6870cd7768', part: "footer" }, h("slot", { key: 'b3e6b451428d1f143668873d4ce7fe6976c19fec', name: "footer" }))));
    }
    static get is() { return "ch-tabular-grid"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["tabular-grid.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tabular-grid.css"]
        };
    }
    static get properties() {
        return {
            "rowSelectionMode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"none\" | \"single\" | \"multiple\"",
                    "resolved": "\"multiple\" | \"none\" | \"single\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "One of \"none\", \"single\" or \"multiple\", indicating how rows can be selected.\nIt can be set to \"none\" if no rows should be selectable,\n\"single\" if only one row can be selected at a time, or\n\"multiple\" if multiple rows can be selected at once."
                },
                "attribute": "row-selection-mode",
                "reflect": false,
                "defaultValue": "\"single\""
            },
            "keyboardNavigationMode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"none\" | \"select\" | \"focus\"",
                    "resolved": "\"focus\" | \"none\" | \"select\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the keyboard navigation mode for the component.\n- \"none\": Disables keyboard navigation for the grid rows.\n- \"select\": Allows keyboard navigation by changing the selection of grid rows.\n- \"focus\": Allows keyboard navigation by focusing on grid rows, but does not change the selection."
                },
                "attribute": "keyboard-navigation-mode",
                "reflect": false,
                "defaultValue": "\"select\""
            },
            "rowHighlightEnabled": {
                "type": "any",
                "mutable": false,
                "complexType": {
                    "original": "boolean | \"auto\"",
                    "resolved": "\"auto\" | boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "One of \"false\", \"true\" or \"auto\", indicating whether or not rows can be highlighted.\n\"auto\", row highlighting will be enabled if the row selection mode is set to \"single\" or \"multiple\"."
                },
                "attribute": "row-highlight-enabled",
                "reflect": false,
                "defaultValue": "\"auto\""
            },
            "rowSelectedClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name applied to a row when it is selected."
                },
                "attribute": "row-selected-class",
                "reflect": false
            },
            "rowHighlightedClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name applied to a row when it is hovered."
                },
                "attribute": "row-highlighted-class",
                "reflect": false
            },
            "rowFocusedClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name applied to a row when it is focused."
                },
                "attribute": "row-focused-class",
                "reflect": false
            },
            "rowMarkedClass": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A CSS class name applied to a row when it is marked."
                },
                "attribute": "row-marked-class",
                "reflect": false
            },
            "showLines": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "| \"all\"\n    | \"all-inside\"\n    | \"column\"\n    | \"column-inside\"\n    | \"none\"\n    | \"row\"\n    | \"row-inside\"",
                    "resolved": "\"all\" | \"all-inside\" | \"column\" | \"column-inside\" | \"none\" | \"row\" | \"row-inside\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Defines which lines (or borders) are displayed within the tabular grid.\nSimilar to the border options in spreadsheet applications, this property\ncontrols whether lines appear around and/or between rows and columns.\n\n**Note:** At the moment, this property does not affect the rendering of\nthe grid. It only reflects the property value as an HTML attribute.\n\n- \"all\": Lines around and between all rows and columns.\n- \"all-inside\": Lines only between rows and columns.\n- \"column\": Lines around and between columns.\n- \"column-inside\": Lines only between columns.\n- \"none\": No lines at all.\n- \"row\": Lines around and between rows.\n- \"row-inside\": Lines only between rows.\n\nDefault: \"all\"."
                },
                "attribute": "show-lines",
                "reflect": true,
                "defaultValue": "\"all\""
            },
            "allowColumnReorder": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "A boolean indicating whether the user can drag column headers to reorder columns."
                },
                "attribute": "allow-column-reorder",
                "reflect": false,
                "defaultValue": "true"
            },
            "columnResizeMode": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"single\" | \"splitter\"",
                    "resolved": "\"single\" | \"splitter\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "One of \"single\" or \"splitter\", indicating the behavior of column resizing.\n\"single\", resize a single column at a time.\n\"splitter\", when adjusts the width of one column, the neighboring columns\n   are also resized proportionally, maintaining the overall width."
                },
                "attribute": "column-resize-mode",
                "reflect": false,
                "defaultValue": "\"single\""
            },
            "localization": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "GridLocalization",
                    "resolved": "GridLocalization",
                    "references": {
                        "GridLocalization": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/tabular-grid/tabular-grid.tsx",
                            "id": "src/components/tabular-grid/tabular-grid.tsx::GridLocalization"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "An object that contains localized strings for the grid."
                }
            }
        };
    }
    static get states() {
        return {
            "rowFocused": {},
            "cellFocused": {},
            "rowHighlighted": {},
            "rowsMarked": {},
            "rowsSelected": {},
            "cellSelected": {},
            "gridStyle": {}
        };
    }
    static get events() {
        return [{
                "method": "selectionChanged",
                "name": "selectionChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the row selection is changed."
                },
                "complexType": {
                    "original": "TabularGridSelectionChangedEvent",
                    "resolved": "TabularGridSelectionChangedEvent",
                    "references": {
                        "TabularGridSelectionChangedEvent": {
                            "location": "import",
                            "path": "./tabular-grid-types",
                            "id": "src/components/tabular-grid/tabular-grid-types.ts::TabularGridSelectionChangedEvent"
                        }
                    }
                }
            }, {
                "method": "rowMarkingChanged",
                "name": "rowMarkingChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the row marking is changed."
                },
                "complexType": {
                    "original": "TabularGridMarkingChangedEvent",
                    "resolved": "TabularGridMarkingChangedEvent",
                    "references": {
                        "TabularGridMarkingChangedEvent": {
                            "location": "import",
                            "path": "./tabular-grid-types",
                            "id": "src/components/tabular-grid/tabular-grid-types.ts::TabularGridMarkingChangedEvent"
                        }
                    }
                }
            }, {
                "method": "cellSelectionChanged",
                "name": "cellSelectionChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when the cell selection is changed."
                },
                "complexType": {
                    "original": "TabularGridCellSelectionChangedEvent",
                    "resolved": "TabularGridCellSelectionChangedEvent",
                    "references": {
                        "TabularGridCellSelectionChangedEvent": {
                            "location": "import",
                            "path": "./tabular-grid-types",
                            "id": "src/components/tabular-grid/tabular-grid-types.ts::TabularGridCellSelectionChangedEvent"
                        }
                    }
                }
            }, {
                "method": "rowClicked",
                "name": "rowClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when a row is clicked."
                },
                "complexType": {
                    "original": "TabularGridRowClickedEvent",
                    "resolved": "TabularGridRowClickedEvent",
                    "references": {
                        "TabularGridRowClickedEvent": {
                            "location": "import",
                            "path": "./tabular-grid-types",
                            "id": "src/components/tabular-grid/tabular-grid-types.ts::TabularGridRowClickedEvent"
                        }
                    }
                }
            }, {
                "method": "rowDoubleClicked",
                "name": "rowDoubleClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when a row is double clicked."
                },
                "complexType": {
                    "original": "TabularGridRowClickedEvent",
                    "resolved": "TabularGridRowClickedEvent",
                    "references": {
                        "TabularGridRowClickedEvent": {
                            "location": "import",
                            "path": "./tabular-grid-types",
                            "id": "src/components/tabular-grid/tabular-grid-types.ts::TabularGridRowClickedEvent"
                        }
                    }
                }
            }, {
                "method": "rowEnterPressed",
                "name": "rowEnterPressed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when Enter is pressed on a row."
                },
                "complexType": {
                    "original": "TabularGridRowPressedEvent",
                    "resolved": "TabularGridRowPressedEvent",
                    "references": {
                        "TabularGridRowPressedEvent": {
                            "location": "import",
                            "path": "./tabular-grid-types",
                            "id": "src/components/tabular-grid/tabular-grid-types.ts::TabularGridRowPressedEvent"
                        }
                    }
                }
            }, {
                "method": "rowContextMenu",
                "name": "rowContextMenu",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Event emitted when attempts to open a context menu on a row."
                },
                "complexType": {
                    "original": "TabularGridRowContextMenuEvent",
                    "resolved": "TabularGridRowContextMenuEvent",
                    "references": {
                        "TabularGridRowContextMenuEvent": {
                            "location": "import",
                            "path": "./tabular-grid-types",
                            "id": "src/components/tabular-grid/tabular-grid-types.ts::TabularGridRowContextMenuEvent"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "getFocusedRow": {
                "complexType": {
                    "signature": "() => Promise<string>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "Retrieves the rowId of the currently focused row.",
                    "tags": []
                }
            },
            "getHoveredRow": {
                "complexType": {
                    "signature": "() => Promise<string>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "Retrieves the rowId of the currently hovered row.",
                    "tags": []
                }
            },
            "getSelectedRows": {
                "complexType": {
                    "signature": "() => Promise<string[]>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string[]>"
                },
                "docs": {
                    "text": "Retrieves the list of rowId of the selected rows.",
                    "tags": []
                }
            },
            "getMarkedRows": {
                "complexType": {
                    "signature": "() => Promise<string[]>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string[]>"
                },
                "docs": {
                    "text": "Retrieves the list of rowId of the marked rows.",
                    "tags": []
                }
            },
            "getFocusedCell": {
                "complexType": {
                    "signature": "() => Promise<{ cellId: string; rowId: string; columnId: string; }>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ cellId: string; rowId: string; columnId: string; }>"
                },
                "docs": {
                    "text": "Retrieves information about the currently focused cell.",
                    "tags": []
                }
            },
            "getSelectedCell": {
                "complexType": {
                    "signature": "() => Promise<{ cellId: string; rowId: string; columnId: string; }>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ cellId: string; rowId: string; columnId: string; }>"
                },
                "docs": {
                    "text": "Retrieves information about the currently selected cell.",
                    "tags": []
                }
            },
            "selectRow": {
                "complexType": {
                    "signature": "(rowId: string, selected?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to select or deselect."
                        }, {
                            "name": "selected",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to select or deselect the row."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Selects or deselects a row.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to select or deselect."
                        }, {
                            "name": "param",
                            "text": "selected - A boolean indicating whether to select or deselect the row."
                        }]
                }
            },
            "selectAllRows": {
                "complexType": {
                    "signature": "(selected?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "selected",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to select or deselect all rows."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Selects or deselects all rows.",
                    "tags": [{
                            "name": "param",
                            "text": "selected - A boolean indicating whether to select or deselect all rows."
                        }]
                }
            },
            "selectCell": {
                "complexType": {
                    "signature": "(cellId?: string, rowId?: string, columnId?: string, selected?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "cellId",
                            "type": "string",
                            "docs": "- The cellId of the cell to select or deselect."
                        }, {
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row containing the cell."
                        }, {
                            "name": "columnId",
                            "type": "string",
                            "docs": "- The columnId of the column containing the cell."
                        }, {
                            "name": "selected",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to select or deselect the cell."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Select or deselect a cell.\nThe cell can be identified by the cellId parameter or\nby using the rowId and columnId pair.",
                    "tags": [{
                            "name": "param",
                            "text": "cellId - The cellId of the cell to select or deselect."
                        }, {
                            "name": "param",
                            "text": "rowId - The rowId of the row containing the cell."
                        }, {
                            "name": "param",
                            "text": "columnId - The columnId of the column containing the cell."
                        }, {
                            "name": "param",
                            "text": "selected - A boolean indicating whether to select or deselect the cell."
                        }]
                }
            },
            "markRow": {
                "complexType": {
                    "signature": "(rowId: string, marked?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to select or deselect."
                        }, {
                            "name": "marked",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to mark or unmark the row."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Mark or unmark a row.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to select or deselect."
                        }, {
                            "name": "param",
                            "text": "marked - A boolean indicating whether to mark or unmark the row."
                        }]
                }
            },
            "markAllRows": {
                "complexType": {
                    "signature": "(marked?: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "marked",
                            "type": "boolean",
                            "docs": "- A boolean indicating whether to mark or unmark all rows."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Mark or unmark all rows.",
                    "tags": [{
                            "name": "param",
                            "text": "marked - A boolean indicating whether to mark or unmark all rows."
                        }]
                }
            },
            "expandRow": {
                "complexType": {
                    "signature": "(rowId: string) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to expand."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Expands a row, showing its children.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to expand."
                        }]
                }
            },
            "collapseRow": {
                "complexType": {
                    "signature": "(rowId: string) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to collapse."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Collapses a row, hiding its children.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to collapse."
                        }]
                }
            },
            "rowEnsureVisible": {
                "complexType": {
                    "signature": "(rowId: string) => Promise<void>",
                    "parameters": [{
                            "name": "rowId",
                            "type": "string",
                            "docs": "- The rowId of the row to ensure visibility."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Ensures that the row is visible within the control, scrolling the contents of the control if necessary.",
                    "tags": [{
                            "name": "param",
                            "text": "rowId - The rowId of the row to ensure visibility."
                        }]
                }
            },
            "cellEnsureVisible": {
                "complexType": {
                    "signature": "(cellId: string) => Promise<void>",
                    "parameters": [{
                            "name": "cellId",
                            "type": "string",
                            "docs": "- The cellId of the cell to ensure visibility."
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Ensures that the cell is visible within the control, scrolling the contents of the control if necessary.",
                    "tags": [{
                            "name": "param",
                            "text": "cellId - The cellId of the cell to ensure visibility."
                        }]
                }
            },
            "getPreviousRow": {
                "complexType": {
                    "signature": "() => Promise<string | void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string | void>"
                },
                "docs": {
                    "text": "Retrieves the rowId of the previous row relative to the currently selected cell.",
                    "tags": []
                }
            },
            "getNextRow": {
                "complexType": {
                    "signature": "() => Promise<string | void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<string | void>"
                },
                "docs": {
                    "text": "Retrieves the rowId of the next row relative to the currently selected cell.",
                    "tags": []
                }
            },
            "getPreviousCell": {
                "complexType": {
                    "signature": "() => Promise<{ cellId: string; rowId: string; columnId: string; }>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ cellId: string; rowId: string; columnId: string; }>"
                },
                "docs": {
                    "text": "Retrieves information about the previous cell relative to the currently selected cell.",
                    "tags": []
                }
            },
            "getNextCell": {
                "complexType": {
                    "signature": "() => Promise<{ cellId: string; rowId: string; columnId: string; }>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<{ cellId: string; rowId: string; columnId: string; }>"
                },
                "docs": {
                    "text": "Retrieves information about the next cell relative to the currently selected cell.",
                    "tags": []
                }
            },
            "syncRowState": {
                "complexType": {
                    "signature": "(el: HTMLElement) => Promise<void>",
                    "parameters": [{
                            "name": "el",
                            "type": "HTMLElement",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLElement": {
                            "location": "global",
                            "id": "global::HTMLElement"
                        },
                        "HTMLChTabularGridRowElement": {
                            "location": "global",
                            "id": "global::HTMLChTabularGridRowElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Synchronizes the state of a row in the grid.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "rowFocused",
                "methodName": "rowFocusedHandler"
            }, {
                "propName": "cellFocused",
                "methodName": "cellFocusedHandler"
            }, {
                "propName": "rowHighlighted",
                "methodName": "rowHighlightedHandler"
            }, {
                "propName": "rowsMarked",
                "methodName": "rowsMarkedHandler"
            }, {
                "propName": "rowsSelected",
                "methodName": "rowsSelectedHandler"
            }, {
                "propName": "cellSelected",
                "methodName": "cellSelectedHandler"
            }];
    }
    static get listeners() {
        return [{
                "name": "focus",
                "method": "focusHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "blur",
                "method": "blurHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "cellFocused",
                "method": "cellFocusedEventHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "keydown",
                "method": "windowKeyDownHandler",
                "target": "window",
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "keyDownHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mousemove",
                "method": "mouseMoveHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mouseleave",
                "method": "mouseLeaveHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mousedown",
                "method": "mouseDownHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "mouseup",
                "method": "mouseUpHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "click",
                "method": "clickHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "dblclick",
                "method": "dblclickHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "touchstart",
                "method": "touchstartHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "touchend",
                "method": "touchendHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "contextmenu",
                "method": "contextmenuHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnSelectorClicked",
                "method": "columnSelectorClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "cellSelectorClicked",
                "method": "cellSelectorClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "cellRowActionClicked",
                "method": "cellRowActionClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": true
            }, {
                "name": "columnHiddenChanged",
                "method": "columnStyleChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnOrderChanged",
                "method": "columnStyleChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnFreezeChanged",
                "method": "columnStyleChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnSizeChanging",
                "method": "columnStyleChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnFreezeChanged",
                "method": "columnFreezeChangedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnResizeStarted",
                "method": "columnResizeStartedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnResizing",
                "method": "columnResizingHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnResizeFinished",
                "method": "columnResizeFinishedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnDragStarted",
                "method": "columnDragStartHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnDragging",
                "method": "columnDraggingHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "columnDragEnded",
                "method": "columnDragEndHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "rowDragStarted",
                "method": "rowDragStartHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "rowEnsureVisible",
                "method": "rowEnsureVisibleHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "cellEnsureVisible",
                "method": "cellEnsureVisibleHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "settingsShowClicked",
                "method": "settingsShowClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "settingsCloseClicked",
                "method": "settingsCloseClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
